import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { authApi, subscriptionPlanApi, SubscriptionPlan, RegisterData, RegisterConsent } from '../services/api';
import { Check } from 'iconoir-react';
import { PaymentForm } from '../components/PaymentForm';
import { OAuthButtons } from '../components/OAuthButtons';
import '../styles/register.css';

interface PCSLevel {
  code: string;
  libelle: string;
  children?: { [key: string]: PCSLevel };
}

type AccountType = 'individual' | 'company';
type BillingPeriod = 'monthly' | 'yearly';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [pcsData, setPcsData] = useState<PCSLevel[]>([]);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [legalConsents, setLegalConsents] = useState<{ cgu: boolean; cgv: boolean; privacy: boolean }>({
    cgu: false,
    cgv: false,
    privacy: false,
  });

  // UX04 — si on arrive avec ?oauth_token=...&oauth_provider=..., on hydrate le form
  // depuis les infos retournées par /api/oauth/finalize et on désactive le password.
  const oauthToken = searchParams.get('oauth_token');
  const oauthProvider = searchParams.get('oauth_provider');
  const isOAuthFlow = Boolean(oauthToken && oauthProvider);

  const [formData, setFormData] = useState<RegisterData>({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    account_type: 'individual',
    plan_id: 0,
    billing_period: 'monthly',
  });

  useEffect(() => {
    loadPlans();
    loadPCSData();
    if (isOAuthFlow && oauthToken) {
      loadOAuthData(oauthToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOAuthData = async (token: string) => {
    try {
      const response = await api.post('/oauth/finalize', { oauth_token: token });
      setFormData(prev => ({
        ...prev,
        name: response.data.name ?? prev.name,
        email: response.data.email ?? prev.email,
      }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lien OAuth expiré, recommencez la connexion.');
    }
  };

  const loadPlans = async () => {
    try {
      const response = await subscriptionPlanApi.getAll();
      setPlans(response.data.plans);
    } catch (err) {
      console.error('Error loading plans:', err);
      setError('Impossible de charger les plans d\'abonnement');
    }
  };

  const loadPCSData = async () => {
    try {
      const response = await fetch('/pcs2020.json');
      const data = await response.json();
      setPcsData(data);
    } catch (err) {
      console.error('Error loading PCS2020 data:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAccountTypeChange = (type: AccountType) => {
    setFormData(prev => ({ ...prev, account_type: type }));
  };

  const handlePlanSelect = (planId: number) => {
    setFormData(prev => ({ ...prev, plan_id: planId }));
  };

  const handleBillingPeriodChange = (period: BillingPeriod) => {
    setFormData(prev => ({ ...prev, billing_period: period }));
  };

  const validateStep = (step: number): boolean => {
    setError(null);

    switch (step) {
      case 1:
        return true; // Account type selection

      case 2:
        // UX04 — en flow OAuth, l'email/name viennent du provider et il n'y a pas de password.
        if (!formData.name || !formData.email) {
          setError('Veuillez remplir tous les champs obligatoires');
          return false;
        }
        if (!isOAuthFlow) {
          if (!formData.password) {
            setError('Veuillez remplir tous les champs obligatoires');
            return false;
          }
          if (formData.password !== formData.password_confirmation) {
            setError('Les mots de passe ne correspondent pas');
            return false;
          }
          if (formData.password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            return false;
          }
        }
        if (formData.account_type === 'company') {
          // UX01 : SIRET et numero TVA sont optionnels ; code pays retire du formulaire.
          if (!formData.company_name || !formData.billing_address ||
              !formData.billing_city || !formData.billing_postal_code ||
              !formData.billing_contact) {
            setError('Veuillez remplir tous les champs obligatoires pour une société');
            return false;
          }
          // Si le SIRET est fourni, il doit faire 14 chiffres ; sinon on l'accepte vide.
          if (formData.siret && formData.siret.length !== 14) {
            setError('Le SIRET doit contenir 14 chiffres');
            return false;
          }
        }
        return true;

      case 3:
        if (!formData.plan_id) {
          setError('Veuillez sélectionner un plan');
          return false;
        }

        // Validation du formulaire publicitaire pour le plan gratuit
        // Les champs pub ne sont obligatoires QUE si l'utilisateur a consenti à la personnalisation
        const selectedPlan = plans.find(p => p.id === formData.plan_id);
        if (selectedPlan && selectedPlan.has_ads && formData.advertising_consent) {
          if (!formData.advertising_interests || formData.advertising_interests.length < 3) {
            setError('Veuillez sélectionner 3 centres d\'intérêt publicitaires');
            return false;
          }

          if (!formData.pcs_level_1 || !formData.pcs_level_2) {
            setError('Veuillez sélectionner votre catégorie socio-professionnelle');
            return false;
          }

          if (!formData.advertising_age) {
            setError('Veuillez sélectionner votre tranche d\'âge');
            return false;
          }

          if (!formData.advertising_gender) {
            setError('Veuillez sélectionner votre sexe');
            return false;
          }
        }
        return true;

      case 4:
        return true; // RGPD consents

      case 5:
        return true; // Payment (if needed)

      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Si l'utilisateur n'a pas consenti à la personnalisation publicitaire,
      // on ne transmet PAS ses données publicitaires au backend.
      const advertisingFields: Partial<RegisterData> = formData.advertising_consent
        ? {
            advertising_interests: formData.advertising_interests,
            pcs_level_1: formData.pcs_level_1,
            pcs_level_2: formData.pcs_level_2,
            pcs_level_3: formData.pcs_level_3,
            pcs_level_4: formData.pcs_level_4,
            advertising_age: formData.advertising_age,
            advertising_gender: formData.advertising_gender,
          }
        : {
            advertising_interests: undefined,
            pcs_level_1: undefined,
            pcs_level_2: undefined,
            pcs_level_3: undefined,
            pcs_level_4: undefined,
            advertising_age: undefined,
            advertising_gender: undefined,
          };

      const consents: RegisterConsent[] = [
        { type: 'cgu', accepted: legalConsents.cgu },
        { type: 'cgv', accepted: legalConsents.cgv },
        { type: 'privacy_policy', accepted: legalConsents.privacy },
      ];

      const selectedPlanForConsent = plans.find(p => p.id === formData.plan_id);
      if (selectedPlanForConsent?.has_ads) {
        consents.push({
          type: 'advertising_policy',
          accepted: Boolean(formData.advertising_consent),
        });
      }

      const dataWithConsents: RegisterData = {
        ...formData,
        ...advertisingFields,
        advertising_consent: Boolean(formData.advertising_consent),
        consents,
        ...(oauthToken ? { oauth_token: oauthToken } : {}),
      };
      // C01 — Sanctum SPA : pas besoin de stocker le token, la session est gérée par cookie httpOnly
      const response = await authApi.register(dataWithConsents);

      // Redirect to email verification page
      navigate('/verify-email', {
        state: {
          email: formData.email,
          message: response.data.message
        }
      });
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Une erreur est survenue lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === formData.plan_id);
  const isPremiumPlan = selectedPlan && !selectedPlan.has_ads;

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-header">
          <img src="/assets/ventilio.png" alt="Ventilio" className="register-logo" />
          <h1>Créer un compte Ventilio</h1>
          <div className="step-indicator">
            {[1, 2, 3, 4, isPremiumPlan ? 5 : null].filter(Boolean).map((step) => (
              <div 
                key={step} 
                className={`step ${currentStep === step ? 'active' : ''} ${currentStep > step! ? 'completed' : ''}`}
              >
                {currentStep > step! ? <Check /> : step}
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="register-form">
          {currentStep === 1 && (
            <StepAccountType
              accountType={formData.account_type}
              onSelect={handleAccountTypeChange}
              onNext={nextStep}
              showOAuth={!isOAuthFlow}
            />
          )}

          {currentStep === 2 && (
            <StepUserInfo
              formData={formData}
              onChange={handleInputChange}
              onNext={nextStep}
              onPrev={prevStep}
              isOAuthFlow={isOAuthFlow}
              oauthProvider={oauthProvider}
            />
          )}

          {currentStep === 3 && (
            <StepPlanSelection 
              plans={plans}
              selectedPlanId={formData.plan_id}
              billingPeriod={formData.billing_period}
              formData={formData}
              onSelectPlan={handlePlanSelect}
              onSelectBillingPeriod={handleBillingPeriodChange}
              onInputChange={handleInputChange}
              onUpdateFormData={(updates) => setFormData(prev => ({ ...prev, ...updates }))}
              onNext={nextStep}
              onPrev={prevStep}
            />
          )}

          {currentStep === 4 && (
            <StepConsents
              selectedPlan={selectedPlan}
              onNext={isPremiumPlan ? nextStep : handleSubmit}
              onPrev={prevStep}
              loading={loading}
              onConsentsChange={setLegalConsents}
            />
          )}

          {currentStep === 5 && isPremiumPlan && (
            <StepPayment 
              selectedPlan={selectedPlan}
              billingPeriod={formData.billing_period}
              onSubmit={handleSubmit}
              onPrev={prevStep}
              loading={loading}
              onPaymentSuccess={(id) => setPaymentId(id)}
              onPaymentError={(err) => setError(err)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface StepAccountTypeProps {
  accountType: AccountType;
  onSelect: (type: AccountType) => void;
  onNext: () => void;
  showOAuth?: boolean;
}

const StepAccountType: React.FC<StepAccountTypeProps> = ({ accountType, onSelect, onNext, showOAuth }) => {
  return (
    <div className="step-content">
      <h2>Type de compte</h2>
      <p>Sélectionnez le type de compte qui vous correspond</p>

      <div className="account-type-options">
        <div
          className={`account-type-card ${accountType === 'individual' ? 'selected' : ''}`}
          onClick={() => onSelect('individual')}
        >
          <h3>Particulier</h3>
          <p>Pour un usage personnel</p>
        </div>

        <div
          className={`account-type-card ${accountType === 'company' ? 'selected' : ''}`}
          onClick={() => onSelect('company')}
        >
          <h3>Société</h3>
          <p>Pour un usage professionnel avec facturation</p>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-primary" onClick={onNext}>
          Suivant
        </button>
      </div>

      {showOAuth && <OAuthButtons mode="register" />}
    </div>
  );
};

interface StepUserInfoProps {
  formData: RegisterData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onNext: () => void;
  onPrev: () => void;
  isOAuthFlow?: boolean;
  oauthProvider?: string | null;
}

const StepUserInfo: React.FC<StepUserInfoProps> = ({ formData, onChange, onNext, onPrev, isOAuthFlow, oauthProvider }) => {
  return (
    <div className="step-content">
      <h2>Informations {formData.account_type === 'company' ? 'de la société' : 'personnelles'}</h2>
      <h3>{formData.account_type === 'company' ? 'Représentant' : ''}</h3>

      {isOAuthFlow && (
        <div className="oauth-notice">
          Connexion via <strong>{oauthProvider === 'microsoft' ? 'Microsoft' : 'Google'}</strong> :
          votre nom et email ont été pré-remplis, et aucun mot de passe n'est nécessaire.
        </div>
      )}

      <div className="form-group">
        <label>Nom complet *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={onChange}
          placeholder="Jean Dupont"
          required
        />
      </div>

      <div className="form-group">
        <label>Email *</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={onChange}
          placeholder="jean.dupont@example.com"
          required
          disabled={isOAuthFlow}
        />
      </div>

      {!isOAuthFlow && (
        <>
          <div className="form-group">
            <label>Mot de passe *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={onChange}
              placeholder="Minimum 8 caractères"
              required
            />
          </div>

          <div className="form-group">
            <label>Confirmer le mot de passe *</label>
            <input
              type="password"
              name="password_confirmation"
              value={formData.password_confirmation}
              onChange={onChange}
              placeholder="Confirmez votre mot de passe"
              required
            />
          </div>
        </>
      )}

      {formData.account_type === 'company' && (
        <>
          <h3>Informations de facturation</h3>
          
          <div className="form-group">
            <label>Nom de la société *</label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name || ''}
              onChange={onChange}
              required
            />
          </div>

          <div className="form-group">
            <label>SIRET (14 chiffres, optionnel)</label>
            <input
              type="text"
              name="siret"
              value={formData.siret || ''}
              onChange={onChange}
              placeholder="12345678901234"
              maxLength={14}
            />
          </div>

          <div className="form-group">
            <label>Adresse de facturation *</label>
            <input
              type="text"
              name="billing_address"
              value={formData.billing_address || ''}
              onChange={onChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Ville *</label>
              <input
                type="text"
                name="billing_city"
                value={formData.billing_city || ''}
                onChange={onChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Code postal *</label>
              <input
                type="text"
                name="billing_postal_code"
                value={formData.billing_postal_code || ''}
                onChange={onChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Contact facturation *</label>
            <input
              type="text"
              name="billing_contact"
              value={formData.billing_contact || ''}
              onChange={onChange}
              placeholder="Nom du contact"
              required
            />
          </div>

          <div className="form-group">
            <label>Numéro de TVA (optionnel)</label>
            <input
              type="text"
              name="vat_number"
              value={formData.vat_number || ''}
              onChange={onChange}
              placeholder="FR12345678901"
            />
          </div>
        </>
      )}

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          Précédent
        </button>
        <button className="btn btn-primary" onClick={onNext}>
          Suivant
        </button>
      </div>
    </div>
  );
};

interface StepPlanSelectionProps {
  plans: SubscriptionPlan[];
  selectedPlanId: number;
  billingPeriod: BillingPeriod;
  formData: RegisterData;
  onSelectPlan: (planId: number) => void;
  onSelectBillingPeriod: (period: BillingPeriod) => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onUpdateFormData: (updates: Partial<RegisterData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

const StepPlanSelection: React.FC<StepPlanSelectionProps> = ({
  plans,
  selectedPlanId,
  billingPeriod,
  formData,
  onSelectPlan,
  onSelectBillingPeriod,
  onInputChange,
  onUpdateFormData,
  onNext,
  onPrev
}) => {
  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const isFreePlan = selectedPlan?.has_ads || false;

  const [planBillingPeriods, setPlanBillingPeriods] = useState<{ [key: number]: BillingPeriod }>({});

  const getPlanBillingPeriod = (planId: number): BillingPeriod => {
    return planBillingPeriods[planId] || 'monthly';
  };

  const setPlanBillingPeriod = (planId: number, period: BillingPeriod) => {
    setPlanBillingPeriods(prev => ({ ...prev, [planId]: period }));
  };

  return (
    <div className="step-content">
      <h2>Choisissez votre plan</h2>

      <div className="plan-education-text">
        <h3>Comprendre nos tarifs</h3>
        <p>
          Développer et maintenir une application comme Ventilio représente des coûts importants : 
          serveurs, stockage, bande passante, développement, maintenance, support client, et conformité RGPD.
        </p>
        <p>
          Pour vous offrir une option 100% gratuite, nous proposons un modèle financé par la publicité. 
          Les revenus publicitaires nous permettent de couvrir ces coûts et de continuer à améliorer Ventilio pour tous.
        </p>
        <p>
          Si vous préférez une expérience sans publicité, nos plans Premium vous offrent 
          une interface épurée et des fonctionnalités avancées.
        </p>
        <div className="plan-reversibility-notice">
          Votre choix est réversible à tout moment et vous pourrez passer d'un plan à l'autre quand vous le souhaitez
        </div>
      </div>

      <div className="plans-grid">
        {plans.map(plan => {
          const hasBothPrices = plan.price_monthly > 0 && plan.price_yearly > 0;
          const currentPeriod = getPlanBillingPeriod(plan.id);
          const currentPrice = currentPeriod === 'monthly' ? plan.price_monthly : plan.price_yearly;
          const displayPrice = typeof currentPrice === 'number' ? currentPrice : parseFloat(currentPrice || '0');
          const priceLabel = currentPeriod === 'monthly' ? '/mois' : '/an';

          return (
            <div
              key={plan.id}
              className={`plan-card ${selectedPlanId === plan.id ? 'selected' : ''} ${plan.has_ads ? 'free' : 'premium'}`}
            >
              {plan.has_ads && <div className="plan-badge">Gratuit</div>}
              {!plan.has_ads && <div className="plan-badge premium">Premium</div>}
              
              <h3>{plan.display_name}</h3>
              <p className="plan-description">{plan.description}</p>

              {hasBothPrices && (
                <div className="billing-period-toggle">
                  <button
                    className={`toggle-btn ${currentPeriod === 'monthly' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanBillingPeriod(plan.id, 'monthly');
                    }}
                  >
                    Mensuel
                  </button>
                  <button
                    className={`toggle-btn ${currentPeriod === 'yearly' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPlanBillingPeriod(plan.id, 'yearly');
                    }}
                  >
                    Annuel
                  </button>
                </div>
              )}
              
              <div className="plan-price">
                <span className="price">{displayPrice.toFixed(2)}€</span>
                <span className="period">{priceLabel}</span>
              </div>

              {hasBothPrices && currentPeriod === 'yearly' && (
                <div className="plan-savings">
                  Économisez {((plan.price_monthly * 12) - plan.price_yearly).toFixed(2)}€/an
                </div>
              )}

              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index}>
                    <Check width={16} height={16} /> {feature}
                  </li>
                ))}
              </ul>

              <button
                className="btn btn-select-plan"
                onClick={() => {
                  onSelectPlan(plan.id);
                  onUpdateFormData({ billing_period: currentPeriod });
                }}
              >
                {selectedPlanId === plan.id ? 'Plan sélectionné' : 'Choisir ce plan'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Consentement RGPD à la personnalisation publicitaire - uniquement pour le plan gratuit */}
      {isFreePlan && selectedPlanId && (
        <div className="advertising-consent-gate">
          <h3>Personnaliser vos publicités (optionnel)</h3>
          <p className="form-intro">
            Le plan gratuit affiche des publicités. Vous pouvez choisir de nous transmettre quelques
            informations (centres d'intérêt, catégorie socio-professionnelle, tranche d'âge, sexe)
            afin de recevoir des publicités plus pertinentes. Vous pouvez aussi refuser : le plan
            gratuit reste accessible et vous verrez alors des publicités non personnalisées.
          </p>
          <p className="form-intro">
            Ces données sont traitées en interne par Ventilio uniquement à des fins publicitaires.
            Elles ne sont ni vendues ni partagées avec des annonceurs. Pour en savoir plus, consultez
            notre <a href="/advertising-policy" target="_blank" rel="noreferrer">Politique de publicité</a>.
          </p>
          <label className="consent-item">
            <input
              type="checkbox"
              checked={Boolean(formData.advertising_consent)}
              onChange={(e) => {
                const accepted = e.target.checked;
                onUpdateFormData(
                  accepted
                    ? { advertising_consent: true }
                    : {
                        advertising_consent: false,
                        advertising_interests: [],
                        pcs_level_1: '',
                        pcs_level_2: '',
                        pcs_level_3: '',
                        pcs_level_4: '',
                        advertising_age: undefined,
                        advertising_gender: undefined,
                      }
                );
              }}
            />
            <span>
              J'accepte la collecte de mes données pour la personnalisation des publicités affichées
              dans Ventilio, conformément à la <a href="/advertising-policy" target="_blank" rel="noreferrer">Politique de publicité</a>.
            </span>
          </label>
        </div>
      )}

      {/* Formulaire de profil publicitaire - uniquement si plan gratuit ET consentement donné */}
      {isFreePlan && selectedPlanId && formData.advertising_consent && (
        <div className="advertising-profile-form">
          <h3>Personnalisez votre expérience publicitaire</h3>
          <p className="form-intro">
            Pour rendre votre expérience publicitaire <strong>plus pertinente et moins pénible</strong>,
            nous collectons quelques informations sur votre profil. Ces données permettent d'afficher
            des publicités adaptées à vos centres d'intérêt, rendant ainsi leur présence moins intrusive.
            Elles sont utilisées uniquement à des fins publicitaires et restent strictement confidentielles.
          </p>

          <div className="form-group">
            <label>3 principaux centres d'intérêt publicitaires *</label>
            <p className="field-help">Sélectionnez les thématiques qui vous intéressent le plus</p>
            <div className="interests-grid">
              {['Technologie', 'Business', 'Marketing', 'Design', 'Développement', 'Productivité', 
                'Formation', 'E-commerce', 'Finance', 'Santé', 'Sport', 'Voyage'].map(interest => (
                <label key={interest} className="interest-checkbox">
                  <input
                    type="checkbox"
                    value={interest}
                    checked={formData.advertising_interests?.includes(interest) || false}
                    onChange={(e) => {
                      const current = formData.advertising_interests || [];
                      const newInterests = e.target.checked
                        ? [...current, interest].slice(0, 3)
                        : current.filter((i: string) => i !== interest);
                      onUpdateFormData({ advertising_interests: newInterests });
                    }}
                    disabled={!formData.advertising_interests?.includes(interest) && 
                             (formData.advertising_interests?.length || 0) >= 3}
                  />
                  <span>{interest}</span>
                </label>
              ))}
            </div>
            <small className="selected-count">
              {formData.advertising_interests?.length || 0}/3 sélectionnés
            </small>
          </div>

          {/* Nomenclature PCS2020 - 4 niveaux en cascade */}
          <div className="form-group">
            <label>Catégorie socio-professionnelle (Niveau 1) *</label>
            <select
              name="pcs_level_1"
              value={formData.pcs_level_1 || ''}
              onChange={(e) => {
                onUpdateFormData({ 
                  pcs_level_1: e.target.value,
                  pcs_level_2: '',
                  pcs_level_3: '',
                  pcs_level_4: ''
                });
              }}
              required
            >
              <option value="">Sélectionnez...</option>
              <option value="1000">Agriculteurs</option>
              <option value="2000">Artisans, commerçants et chefs d'entreprise</option>
              <option value="3000">Cadres</option>
              <option value="4000">Professions intermédiaires</option>
              <option value="5000">Employés</option>
              <option value="6000">Ouvriers</option>
            </select>
          </div>

          {formData.pcs_level_1 && (
            <div className="form-group">
              <label>Catégorie (Niveau 2) *</label>
              <select
                name="pcs_level_2"
                value={formData.pcs_level_2 || ''}
                onChange={(e) => {
                  onUpdateFormData({ 
                    pcs_level_2: e.target.value,
                    pcs_level_3: '',
                    pcs_level_4: ''
                  });
                }}
                required
              >
                <option value="">Sélectionnez...</option>
                {formData.pcs_level_1 === '1000' && (
                  <option value="1000">Exploitants de l'agriculture, sylviculture, pêche et aquaculture</option>
                )}
                {formData.pcs_level_1 === '2000' && (
                  <>
                    <option value="2100">Artisans</option>
                    <option value="2200">Commerçants et assimilés</option>
                    <option value="2300">Chefs d'entreprise de plus de 10 personnes</option>
                  </>
                )}
                {formData.pcs_level_1 === '3000' && (
                  <>
                    <option value="3100">Professions libérales</option>
                    <option value="3300">Cadres de la fonction publique</option>
                    <option value="3400">Professeurs et professions scientifiques</option>
                    <option value="3500">Professions de l'information, de l'art et des spectacles</option>
                    <option value="3700">Cadres administratifs et commerciaux</option>
                    <option value="3800">Cadres techniques d'entreprise</option>
                  </>
                )}
                {formData.pcs_level_1 === '4000' && (
                  <>
                    <option value="4200">Professions de l'enseignement primaire et professionnel</option>
                    <option value="4300">Intermédiaires de la santé et du travail social</option>
                    <option value="4400">Religieux</option>
                    <option value="4500">Intermédiaires de la fonction publique</option>
                    <option value="4600">Intermédiaires des entreprises</option>
                    <option value="4700">Techniciens</option>
                    <option value="4800">Agents de maîtrise de production</option>
                  </>
                )}
                {formData.pcs_level_1 === '5000' && (
                  <>
                    <option value="5200">Employés de la fonction publique</option>
                    <option value="5300">Policiers, militaires et agents de sécurité privée</option>
                    <option value="5400">Employés administratifs d'entreprise</option>
                    <option value="5500">Employés de commerce</option>
                    <option value="5600">Personnels des services directs aux particuliers</option>
                  </>
                )}
              </select>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Âge *</label>
              <select
                name="advertising_age"
                value={formData.advertising_age || ''}
                onChange={onInputChange}
                required
              >
                <option value="">Sélectionnez...</option>
                <option value="18">18-24 ans</option>
                <option value="25">25-34 ans</option>
                <option value="35">35-44 ans</option>
                <option value="45">45-54 ans</option>
                <option value="55">55-64 ans</option>
                <option value="65">65 ans et plus</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Sexe *</label>
            <div className="gender-options">
              {[
                { value: 'male', label: 'Homme' },
                { value: 'female', label: 'Femme' },
                { value: 'other', label: 'Autre' },
                { value: 'prefer_not_to_say', label: 'Préfère ne pas répondre' }
              ].map(option => (
                <label key={option.value} className="radio-option">
                  <input
                    type="radio"
                    name="advertising_gender"
                    value={option.value}
                    checked={formData.advertising_gender === option.value}
                    onChange={onInputChange}
                    required
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="privacy-notice">
            Ces informations sont utilisées uniquement pour personnaliser les publicités affichées. 
            Elles ne sont jamais vendues à des tiers et vous pouvez les modifier à tout moment dans vos paramètres.
          </div>
        </div>
      )}

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          Précédent
        </button>
        <button 
          className="btn btn-primary" 
          onClick={onNext}
          disabled={!selectedPlanId}
        >
          Suivant
        </button>
      </div>
    </div>
  );
};

interface StepConsentsProps {
  selectedPlan?: SubscriptionPlan;
  onNext: () => void;
  onPrev: () => void;
  loading: boolean;
  onConsentsChange: (consents: { cgu: boolean; cgv: boolean; privacy: boolean }) => void;
}

const StepConsents: React.FC<StepConsentsProps> = ({ selectedPlan, onNext, onPrev, loading, onConsentsChange }) => {
  const [consents, setConsents] = useState({
    cgu: false,
    cgv: false,
    privacy: false,
  });

  const handleConsentChange = (key: keyof typeof consents) => {
    const updated = { ...consents, [key]: !consents[key] };
    setConsents(updated);
    onConsentsChange(updated);
  };

  const allConsentsGiven = consents.cgu && consents.cgv && consents.privacy;

  return (
    <div className="step-content">
      <h2>Consentements RGPD</h2>
      <p>Veuillez accepter les conditions suivantes pour continuer</p>

      <div className="consents-list">
        <label className="consent-item">
          <input
            type="checkbox"
            checked={consents.cgu}
            onChange={() => handleConsentChange('cgu')}
          />
          <span>
            J'accepte les <a href="/cgu" target="_blank" rel="noreferrer">Conditions Générales d'Utilisation</a> *
          </span>
        </label>

        <label className="consent-item">
          <input
            type="checkbox"
            checked={consents.cgv}
            onChange={() => handleConsentChange('cgv')}
          />
          <span>
            J'accepte les <a href="/cgv" target="_blank" rel="noreferrer">Conditions Générales de Vente</a> *
          </span>
        </label>

        <label className="consent-item">
          <input
            type="checkbox"
            checked={consents.privacy}
            onChange={() => handleConsentChange('privacy')}
          />
          <span>
            J'accepte la <a href="/privacy" target="_blank" rel="noreferrer">Politique de Confidentialité</a> *
          </span>
        </label>

        {selectedPlan?.has_ads && (
          <p className="consent-info">
            Le plan gratuit affiche des publicités. Votre consentement à la personnalisation publicitaire
            a été enregistré à l'étape précédente.
          </p>
        )}
      </div>

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onPrev}>
          Précédent
        </button>
        <button 
          className="btn btn-primary" 
          onClick={onNext}
          disabled={!allConsentsGiven || loading}
        >
          {loading ? 'Inscription...' : (selectedPlan?.has_ads ? 'Créer mon compte' : 'Suivant')}
        </button>
      </div>
    </div>
  );
};

interface StepPaymentProps {
  selectedPlan?: SubscriptionPlan;
  billingPeriod: BillingPeriod;
  onSubmit: () => void;
  onPrev: () => void;
  loading: boolean;
  onPaymentSuccess: (paymentId: string) => void;
  onPaymentError: (error: string) => void;
}

const StepPayment: React.FC<StepPaymentProps> = ({ 
  selectedPlan, 
  billingPeriod, 
  onSubmit, 
  onPrev, 
  loading,
  onPaymentSuccess,
  onPaymentError
}) => {
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  if (!selectedPlan) return null;

  const rawAmount = billingPeriod === 'monthly' ? selectedPlan.price_monthly : selectedPlan.price_yearly;
  const amount = typeof rawAmount === 'number' ? rawAmount : parseFloat(rawAmount || '0');
  const periodLabel = billingPeriod === 'monthly' ? 'Mensuel' : 'Annuel';

  const handlePaymentSuccess = (paymentId: string) => {
    setPaymentCompleted(true);
    setPaymentError(null);
    onPaymentSuccess(paymentId);
    // Auto-submit after successful payment
    setTimeout(() => {
      onSubmit();
    }, 1000);
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(error);
    onPaymentError(error);
  };

  return (
    <div className="step-content">
      <h2>Paiement sécurisé</h2>
      <p>Finalisez votre inscription au plan {selectedPlan.display_name}</p>

      <div className="payment-summary">
        <div className="summary-row">
          <span>Plan {selectedPlan.display_name}</span>
          <span>{amount.toFixed(2)}€</span>
        </div>
        <div className="summary-row">
          <span>Période</span>
          <span>{periodLabel}</span>
        </div>
        <div className="summary-row total">
          <span>Total à payer</span>
          <span>{amount.toFixed(2)}€</span>
        </div>
      </div>

      {paymentError && (
        <div className="payment-error">
          {paymentError}
        </div>
      )}

      {paymentCompleted && (
        <div className="payment-success">
          Paiement réussi ! Création de votre compte en cours...
        </div>
      )}

      {!paymentCompleted && (
        <div className="payment-info">
          <p>
            <strong>Information :</strong> Votre paiement sera traité de manière sécurisée via Stancer.
            Aucune donnée bancaire n'est stockée sur nos serveurs.
          </p>
        </div>
      )}

      <PaymentForm
        amount={amount}
        planId={selectedPlan.id}
        billingPeriod={billingPeriod}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
      />

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onPrev} disabled={loading || paymentCompleted}>
          Précédent
        </button>
      </div>
    </div>
  );
};
